import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { TriangleAlert } from "lucide-react";
import { ROLES, type Role, type UserItem } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { ROLE_LABEL, checkSelfTarget, checkUserCommand } from "../../shared/permissions";
import { StatusBadge } from "@/components/StatusBadge";
import { TableSkeleton } from "@/components/Loading";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { screens } from "../screens";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { createUser, errorText, fieldErrors, getUsers, patchUser, resetUserPassword } from "@/lib/client";
import { navigate } from "@/lib/nav";
import { formatDay } from "@/lib/upload";

type Act = { kind: "create" } | { kind: "role" | "deactivate" | "reset"; user: UserItem };
type Temp = { title: string; username: string; role: Role; tempPassword: string };

/** Verilebilecek roller: kuralı `checkUserCommand` verir, web kopyalamaz. */
export const grantableRoles = (actor: Role): Role[] => ROLES.filter((r) => checkUserCommand({ actor, action: "create", newRole: r }) === null);
/** Satırı yönetemezse nedeni, yönetebilirse null. */
export function manageNote(actor: Role, me: string, u: UserItem): "self" | "super" | null {
  if (checkSelfTarget(me, u.username)) return "self";
  return checkUserCommand({ actor, action: "set-role", targetRole: u.role }) ? "super" : null;
}

/** E9 Kullanıcılar (ui.md §4.6, ADR-0017). Yetkiyi API denetler; burada yalnız düğmeler ve seçenekler süzülür. */
export function UsersScreen({ me, role }: { me: string; role: Role }) {
  const [items, setItems] = useState<UserItem[] | null>(null);
  const [error, setError] = useState("");
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState("");
  const [act, setAct] = useState<Act | null>(null);
  const [activating, setActivating] = useState<string | null>(null);
  const [actError, setActError] = useState("");
  const createBtn = useRef<HTMLButtonElement>(null);
  const table = useRef<HTMLDivElement>(null);
  const back = useRef<{ user: string; part: string } | null>(null); // dialog opener, to hand focus back after close
  const open = (a: Act, part: string) => { back.current = { user: "user" in a ? a.user.username : "", part }; setAct(a); };

  const load = useCallback(async () => {
    setFetching(true);
    try { setItems((await getUsers()).items); setError(""); } catch (e) { setError(errorText(e)); } finally { setFetching(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  // Focus goes back to the opener once the dialog is gone and the list is re-read; its button may have been recreated
  // (Pasifleştir -> Aktifleştir), so it is looked up by row and part. A vanished row falls back to "Kullanıcı aç".
  useEffect(() => {
    if (act || fetching || activating || !back.current) return;
    const { user, part } = back.current;
    back.current = null;
    const el = user ? table.current?.querySelector<HTMLElement>(`[data-user="${CSS.escape(user)}"][data-part="${part}"]`) : null;
    (el ?? createBtn.current)?.focus();
  }, [act, fetching, items, activating]); // activating: a failed Aktifleştir re-enables its button without a re-read

  async function activate(u: UserItem) {
    setActivating(u.username); setActError(""); setResult("");
    back.current = { user: u.username, part: "toggle" };
    try { await patchUser(u.username, { active: true }); setResult(`✓ ${u.username} aktifleştirildi.`); await load(); } catch (e) { setActError(errorText(e)); } finally { setActivating(null); }
  }

  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader title="Kullanıcılar" description={screens.find((s) => s.id === "E9")!.description}
        actions={<Button ref={createBtn} onClick={() => open({ kind: "create" }, "")}>Kullanıcı aç</Button>} />
      {result && <p role="status" className="text-sm text-success-foreground">{result}</p>}
      {actError && <ErrorAlert>{actError}</ErrorAlert>}
      {error ? <ErrorAlert>{error}</ErrorAlert> : !items ? <TableSkeleton cols={6} /> : (
        <Card ref={table} aria-busy={fetching} className="gap-0 overflow-clip p-0 transition-opacity aria-busy:opacity-50">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground pl-4">Kullanıcı adı</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Rol</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Durum</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Açılış</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Son giriş</TableHead>
                <TableHead><span className="sr-only">İşlemler</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => {
                const note = manageNote(role, me, u);
                return (
                  <TableRow key={u.username}>
                    <TableCell className="pl-4"><span className="flex items-center gap-2"><UserAvatar name={u.username} />{u.username}{note === "self" && <span className="text-muted-foreground"> · siz</span>}</span></TableCell>
                    <TableCell>{ROLE_LABEL[u.role]}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={u.active ? undefined : "text-muted-foreground"}>{u.active ? "Aktif" : "Pasif"}</span>
                        {u.locked && <StatusBadge tone="warning">Kilitli</StatusBadge>}
                        {u.mustSetPassword && <StatusBadge tone="info">Parola belirlenmeli</StatusBadge>}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">{formatDay(u.createdAt)}</TableCell>
                    <TableCell className="tabular-nums">{u.lastLoginAt ? formatAuditTime(u.lastLoginAt) : "—"}</TableCell>
                    <TableCell className="whitespace-nowrap pr-4">
                      {note === "self" ? (
                        <span className="text-xs text-muted-foreground">Kendi hesabınız: <a href="/account" onClick={(e) => navigate(e, "/account")} className="text-primary underline-offset-4 hover:underline">Hesabım</a></span>
                      ) : note === "super" ? (
                        <span className="text-xs text-muted-foreground">Yalnız Super Admin yönetir</span>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" data-user={u.username} data-part="role" aria-label={`${u.username} için rolü değiştir`} onClick={() => open({ kind: "role", user: u }, "role")}>Rolü değiştir</Button>
                          {u.active
                            ? <Button variant="outline" size="sm" data-user={u.username} data-part="toggle" aria-label={`${u.username} için pasifleştir`} onClick={() => open({ kind: "deactivate", user: u }, "toggle")}>Pasifleştir</Button>
                            : <Button variant="outline" size="sm" data-user={u.username} data-part="toggle" aria-label={`${u.username} için aktifleştir`} disabled={activating === u.username} onClick={() => void activate(u)}>{activating === u.username ? "Aktifleştiriliyor…" : "Aktifleştir"}</Button>}
                          <Button variant="outline" size="sm" data-user={u.username} data-part="reset" aria-label={`${u.username} için parola sıfırla`} onClick={() => open({ kind: "reset", user: u }, "reset")}>Parola sıfırla</Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      {act && <UserDialog key={`${act.kind}:${"user" in act ? act.user.username : ""}`} act={act} actor={role} onClose={(changed, msg) => { setAct(null); if (msg) { setResult(msg); setActError(""); } if (changed) void load(); }} />}
    </main>
  );
}

const TITLE = { create: "Kullanıcı aç", role: "Rolü değiştir", deactivate: "pasifleştirilsin mi?", reset: "için parola sıfırlansın mı?" } as const;

/**
 * Tek diyalog: form, onay ya da geçici parola. Geçici parola yalnız bu bileşenin durumundadır (ADR-0017):
 * diyalog kapanınca bileşen kaldırılır, değer gider. URL, localStorage, sorgu önbelleği kullanılmaz.
 */
export function UserDialog({ act, actor, onClose }: { act: Act; actor: Role; onClose: (changed: boolean, message?: string) => void }) {
  const [temp, setTemp] = useState<Temp | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [username, setUsername] = useState("");
  const [newRole, setNewRole] = useState<string>(act.kind === "role" ? act.user.role : "");
  const [copy, setCopy] = useState<"idle" | "done" | "failed">("idle");
  const [changed, setChanged] = useState(false);
  const [fails, setFails] = useState(0);
  const form = useRef<HTMLFormElement>(null);
  const copyBtn = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (temp) copyBtn.current?.focus(); }, [temp]); // the submit button is gone, Radix's opening focus does not run again
  // After a failed submit: first invalid field, else the primary button (it was disabled while busy and lost focus).
  useEffect(() => {
    if (fails) (form.current?.querySelector<HTMLElement>("[aria-invalid=true]") ?? form.current?.querySelector<HTMLElement>("button[type=submit]"))?.focus();
  }, [fails]);
  const roles = grantableRoles(actor);

  const close = () => onClose(changed || temp !== null);
  async function run(fn: () => Promise<string | void>, form: boolean) {
    setBusy(true); setError(""); setErrors({});
    try { const msg = await fn(); if (msg !== undefined) { onClose(true, msg); return; } }
    catch (e) { const fe = form ? fieldErrors(e) : null; if (fe) setErrors(fe); else setError(errorText(e)); setFails((n) => n + 1); }
    finally { setBusy(false); }
  }
  const show = (title: string, r: { username: string; tempPassword: string }, roleOf: Role) => { setTemp({ title, username: r.username, role: roleOf, tempPassword: r.tempPassword }); setChanged(true); };

  function submit(e: FormEvent) {
    e.preventDefault();
    if (act.kind === "create") return void run(async () => show("Kullanıcı açıldı", await createUser({ username, role: newRole as Role }), newRole as Role), true);
    if (act.kind === "role") return void run(async () => { await patchUser(act.user.username, { role: newRole as Role }); return `✓ ${act.user.username}: rol ${ROLE_LABEL[newRole as Role]} oldu.`; }, true);
    if (act.kind === "deactivate") return void run(async () => { await patchUser(act.user.username, { active: false }); return `✓ ${act.user.username} pasifleştirildi.`; }, false);
    return void run(async () => show("Parola sıfırlandı", await resetUserPassword(act.user.username), act.user.role), false);
  }

  async function doCopy() {
    try { await navigator.clipboard.writeText(temp!.tempPassword); setCopy("done"); clearTimeout(timer.current); timer.current = setTimeout(() => setCopy("idle"), 2000); } catch { setCopy("failed"); }
  }

  const title = temp ? temp.title : act.kind === "create" ? TITLE.create : act.kind === "role" ? `${TITLE.role}: ${act.user.username}` : `${act.user.username} ${act.kind === "deactivate" ? "pasifleştirilsin mi?" : TITLE.reset}`;
  const primary = { create: ["Aç", "Açılıyor…"], role: ["Rolü kaydet", "Kaydediliyor…"], deactivate: ["Pasifleştir", "Pasifleştiriliyor…"], reset: ["Parolayı sıfırla", "Sıfırlanıyor…"] }[act.kind];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent showCloseButton={false} className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()} onInteractOutside={temp ? (e) => e.preventDefault() : undefined} onEscapeKeyDown={temp ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {!temp && act.kind === "deactivate" && <DialogDescription>{act.user.username} bir sonraki işleminde oturumdan çıkar ve giriş yapamaz. Kayıtlarda ve denetim logunda adı kalır. Gerekirse yeniden aktifleştirilir.</DialogDescription>}
          {!temp && act.kind === "reset" && <DialogDescription>Mevcut parola geçersiz olur, açık oturumları kapanır, kilit varsa açılır. Yeni geçici parola bir kez gösterilir.</DialogDescription>}
          {!temp && act.kind === "role" && <DialogDescription>Şu anki rol: {ROLE_LABEL[act.user.role]}</DialogDescription>}
          {!temp && act.kind === "create" && <DialogDescription className="sr-only">Yeni kullanıcının adı ve rolü</DialogDescription>}
          {temp && <DialogDescription>{temp.username} · {ROLE_LABEL[temp.role]} için geçici parola:</DialogDescription>}
        </DialogHeader>
        {temp ? (
          <>
            <div role="group" aria-label="Geçici parola" className="rounded-md border bg-muted p-3 font-mono text-base tracking-wider select-all">{temp.tempPassword}</div>
            <div className="flex flex-col gap-1">
              <Button ref={copyBtn} type="button" variant="outline" size="sm" className="w-fit" onClick={() => void doCopy()}>{copy === "done" ? "Kopyalandı" : "Kopyala"}</Button>
              <p aria-live="polite" className={copy === "failed" ? "text-xs text-muted-foreground" : "sr-only"}>{copy === "done" ? "Kopyalandı" : copy === "failed" ? "Kopyalanamadı. Parolayı seçip kopyalayın." : ""}</p>
            </div>
            <div className="flex gap-2 rounded-md border border-warning-border bg-warning p-3 text-sm text-warning-foreground">
              <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              <p>Bu parola bir daha gösterilmez. Kullanıcıya şimdi iletin. Kaybolursa parolayı yeniden sıfırlayın.</p>
            </div>
            <p className="text-sm text-muted-foreground">Kullanıcı ilk girişte kendi parolasını belirler.</p>
            <DialogFooter><Button type="button" onClick={close}>Kapat</Button></DialogFooter>
          </>
        ) : (
          <form ref={form} onSubmit={submit} className="flex flex-col gap-4">
            {act.kind === "create" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="new-username">Kullanıcı adı</Label>
                <Input id="new-username" className="font-mono" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} required
                  aria-invalid={errors.username ? true : undefined} aria-describedby={errors.username ? "new-username-err" : "new-username-hint"} />
                {!errors.username && <p id="new-username-hint" className="text-xs text-muted-foreground">3-32 karakter: küçük harf, rakam, nokta, alt çizgi, tire.</p>}
                {errors.username && <p id="new-username-err" className="text-sm text-destructive">{errors.username}</p>}
              </div>
            )}
            {(act.kind === "create" || act.kind === "role") && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="user-role">{act.kind === "create" ? "Rol" : "Yeni rol"}</Label>
                <NativeSelect id="user-role" value={newRole} onChange={(e) => setNewRole(e.target.value)} required aria-invalid={errors.role ? true : undefined} aria-describedby={errors.role ? "user-role-err" : undefined}>
                  {act.kind === "create" && <option value="">Rol seçin</option>}
                  {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </NativeSelect>
                {errors.role && <p id="user-role-err" className="text-sm text-destructive">{errors.role}</p>}
              </div>
            )}
            {error && <ErrorAlert>{error}</ErrorAlert>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Vazgeç</Button>
              <Button type="submit" disabled={busy || (act.kind === "create" && (!username || !newRole)) || (act.kind === "role" && newRole === act.user.role)}>{busy ? primary[1] : primary[0]}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
